// Helper function to format phone number - removes all non-numeric characters
export const formatPhoneNumber = (phone: string): string => {
  if (!phone) return "";
  // Remove all non-numeric characters (spaces, hyphens, parentheses, plus sign, etc.)
  return phone.replace(/\D/g, "");
};

/**
 * Normalizes a phone number by ensuring it starts with "+" if it contains digits.
 * This is necessary because parsePhoneNumberFromString needs "+" to distinguish country codes.
 * System saves numbers without "+" (e.g., "5571981813232"), so we add it at UI level.
 * 
 * @param phone - The phone number to normalize (can be string, number, null, or undefined)
 * @returns The normalized phone number with "+" prefix, or empty string if no digits found
 */
export const normalizePhoneNumberForParsing = (phone: string | number | null | undefined): string => {
  if (!phone) return "";
  
  let normalized = String(phone).trim();
  if (normalized && !normalized.startsWith("+")) {
    const digitsOnly = normalized.replace(/[^\d]/g, "");
    if (digitsOnly) {
      normalized = "+" + digitsOnly;
    } else {
      return "";
    }
  }
  return normalized;
};

/**
 * Formats a phone number with international mask (country code + area code + number)
 * Supports multiple countries with intelligent formatting
 * 
 * @param phone - The phone number to format (can include or not include country code)
 * @returns Formatted phone number like "+55 (61) 93494-9991" or original if can't format
 */
export const formatPhoneNumberWithMask = (phone: string | number | null | undefined): string => {
  if (!phone) return "";
  
  // Remove all non-numeric characters
  const digitsOnly = String(phone).replace(/\D/g, "");
  
  if (!digitsOnly) return "";
  
  // Brazil (55) - Most common case
  if (digitsOnly.startsWith("55") && digitsOnly.length >= 12) {
    const countryCode = digitsOnly.substring(0, 2);
    const areaCode = digitsOnly.substring(2, 4);
    const number = digitsOnly.substring(4);
    
    // Mobile (9 digits): +55 (61) 93494-9991
    if (number.length === 9) {
      return `+${countryCode} (${areaCode}) ${number.substring(0, 5)}-${number.substring(5)}`;
    }
    // Landline (8 digits): +55 (61) 3494-9991
    else if (number.length === 8) {
      return `+${countryCode} (${areaCode}) ${number.substring(0, 4)}-${number.substring(4)}`;
    }
  }
  
  // USA/Canada (1) - Format: +1 (555) 123-4567
  if (digitsOnly.startsWith("1") && digitsOnly.length === 11) {
    const countryCode = digitsOnly.substring(0, 1);
    const areaCode = digitsOnly.substring(1, 4);
    const number = digitsOnly.substring(4);
    return `+${countryCode} (${areaCode}) ${number.substring(0, 3)}-${number.substring(3)}`;
  }
  
  // Argentina (54) - Format: +54 9 11 1234-5678
  if (digitsOnly.startsWith("54") && digitsOnly.length >= 12) {
    const countryCode = digitsOnly.substring(0, 2);
    const carrier = digitsOnly.substring(2, 3);
    const areaCode = digitsOnly.substring(3, 5);
    const number = digitsOnly.substring(5);
    return `+${countryCode} ${carrier} ${areaCode} ${number.substring(0, 4)}-${number.substring(4)}`;
  }
  
  // Mexico (52) - Format: +52 55 1234-5678
  if (digitsOnly.startsWith("52") && digitsOnly.length >= 12) {
    const countryCode = digitsOnly.substring(0, 2);
    const areaCode = digitsOnly.substring(2, 4);
    const number = digitsOnly.substring(4);
    return `+${countryCode} ${areaCode} ${number.substring(0, 4)}-${number.substring(4)}`;
  }
  
  // Generic international format for other countries
  // Format: +XX XXX XXX XXXX
  if (digitsOnly.length >= 10) {
    const countryCode = digitsOnly.substring(0, 2);
    const rest = digitsOnly.substring(2);
    const formatted = rest.match(/.{1,4}/g)?.join(" ") || rest;
    return `+${countryCode} ${formatted}`;
  }
  
  // If can't format, return with + prefix
  return `+${digitsOnly}`;
};
